// src/pages/Signup.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccessibility } from '../contexts/AccessibilityContext';
import AacButton from '../components/aac/AacButton';
import './Signup.css';
import { useFocusableNavigation } from '../hooks/useFocusableNavigation';

const Signup = () => {
  const { signUp } = useAuth();
  const { announce } = useAccessibility();
  useFocusableNavigation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (password.length < 6) {
      const message = 'Please use a password with at least 6 characters.';
      setError(message);
      announce(message);
      return;
    }

    setLoading(true);
    const { error: signUpError } = await signUp(email, password);
    setLoading(false);

    if (signUpError) {
      const message = signUpError.message.includes('already registered')
        ? 'An account with that email already exists. Try logging in instead.'
        : 'Something went wrong creating your account. Please try again.';
      setError(message);
      announce(message);
      return;
    }

    announce('Account created successfully');
    navigate('/', { replace: true });
  };

  // The submit button is type="submit" so a real click/Enter/Space already
  // triggers the form's onSubmit natively (required so Enter-in-any-field
  // still submits the form for switch/AAC users). Only the digit-1 AAC
  // shortcut needs to call handleSubmit directly, since it never produces a
  // real click and so never triggers native form submission on its own.
  const handleShortcutSubmit = (event) => {
    if (event.type === 'click') return;
    handleSubmit(event);
  };

  return (
    <div className="auth-page">
      <h1>Create Account</h1>
      <p className="auth-page__intro">Sign up to save your agenda and access your account anywhere.</p>

      <form className="auth-page__form" onSubmit={handleSubmit}>
        <label htmlFor="signup-email">Email</label>
        <input
          id="signup-email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          autoComplete="email"
        />

        <label htmlFor="signup-password">Password</label>
        <input
          id="signup-password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
          autoComplete="new-password"
          minLength={6}
        />

        {error && <p className="auth-page__error" role="alert">{error}</p>}

        <div className="auth-page__actions">
          <AacButton type="submit" shortcut={1} onClick={handleShortcutSubmit} disabled={loading}>
            {loading ? 'Creating account…' : 'Create Account'}
          </AacButton>
        </div>
      </form>

      <div className="auth-page__links">
        <Link to="/login">Already have an account? Log in</Link>
      </div>
    </div>
  );
};

export default Signup;
